import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { redirectToExternalUrl } from '@/lib/externalRedirect';

// Plan tier mapping
export const SUBSCRIPTION_PLANS = {
  runner: {
    slug: 'runner',
    name: 'Runner',
    priceId: 'price_1SpPBFRw2cw6QnF7Wuz6zSan',
    productId: 'prod_Tmz9cFWbgsoFvL',
    priceCents: 900,
    limits: {
      plan_generations: 2,
      race_preparations: 1
    }
  },
  'elite-runner': {
    slug: 'elite-runner',
    name: 'Elite Runner',
    priceId: 'price_1SpPBcRw2cw6QnF7oxkzxKUv',
    productId: 'prod_Tmz9nuiaj21aAW',
    priceCents: 1600,
    limits: {
      plan_generations: 4,
      race_preparations: 2
    }
  }
} as const;

export type PlanSlug = keyof typeof SUBSCRIPTION_PLANS;

interface SubscriptionState {
  isLoading: boolean;
  isSubscribed: boolean;
  planSlug: PlanSlug | null;
  productId: string | null;
  subscriptionStart: string | null;
  subscriptionEnd: string | null;
  cancelAtPeriodEnd: boolean;
  usage: Record<string, number>;
  priceCents: number | null;
  billingInterval: string | null;
  billingIntervalCount: number | null;
}

interface UsageRecord {
  feature_type: string;
  count: number;
}

interface CachedSubscription {
  timestamp: number;
  isSubscribed: boolean;
  planSlug: PlanSlug | null;
  productId: string | null;
  subscriptionStart: string | null;
  subscriptionEnd: string | null;
  cancelAtPeriodEnd: boolean;
  priceCents: number | null;
  billingInterval: string | null;
  billingIntervalCount: number | null;
}

const CACHE_KEY = 'subscription_cache';
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

function getCachedSubscription(): CachedSubscription | null {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return null;
    
    const parsed: CachedSubscription = JSON.parse(cached);
    const age = Date.now() - parsed.timestamp;
    
    // Invalidate if older than 24 hours
    if (age > CACHE_TTL_MS) {
      localStorage.removeItem(CACHE_KEY);
      return null;
    }
    
    return parsed;
  } catch {
    return null;
  }
}

function setCachedSubscription(data: Omit<CachedSubscription, 'timestamp'>) {
  try {
    const cached: CachedSubscription = {
      ...data,
      timestamp: Date.now()
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(cached));
  } catch {
    // Ignore storage errors
  }
}

export function clearCachedSubscription() {
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch {
    // Ignore storage errors
  }
}

export function useSubscription() {
  const { toast } = useToast();
  
  // Initialize with cached data if available
  const cached = getCachedSubscription();
  
  const [state, setState] = useState<SubscriptionState>({
    isLoading: !cached, // Not loading if we have valid cache
    isSubscribed: cached?.isSubscribed ?? false,
    planSlug: cached?.planSlug ?? null,
    productId: cached?.productId ?? null,
    subscriptionStart: cached?.subscriptionStart ?? null,
    subscriptionEnd: cached?.subscriptionEnd ?? null,
    cancelAtPeriodEnd: cached?.cancelAtPeriodEnd ?? false,
    usage: {},
    priceCents: cached?.priceCents ?? null,
    billingInterval: cached?.billingInterval ?? null,
    billingIntervalCount: cached?.billingIntervalCount ?? null
  });

  const checkSubscription = useCallback(async (forceRefresh = false) => {
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      // If no session or session error, clear everything
      if (!session || sessionError) {
        clearCachedSubscription();
        setState({
          isLoading: false,
          isSubscribed: false,
          planSlug: null,
          productId: null,
          subscriptionStart: null,
          subscriptionEnd: null,
          cancelAtPeriodEnd: false,
          usage: {},
          priceCents: null,
          billingInterval: null,
          billingIntervalCount: null
        });
        return;
      }

      // Check if we have valid cache and don't need to refresh
      const cachedData = getCachedSubscription();
      if (!forceRefresh && cachedData) {
        const age = Date.now() - cachedData.timestamp;
        // If cache is less than 24 hours old, use it and fetch usage only
        if (age < CACHE_TTL_MS) {
          // Fetch current usage (always fresh)
          const now = new Date();
          const periodStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
          
          const { data: usageData } = await supabase
            .from('usage_tracking')
            .select('feature_type, count')
            .eq('user_id', session.user.id)
            .gte('period_start', periodStart);

          const usage: Record<string, number> = {};
          if (usageData) {
            (usageData as UsageRecord[]).forEach((record) => {
              usage[record.feature_type] = record.count;
            });
          }

          setState({
            isLoading: false,
            isSubscribed: cachedData.isSubscribed,
            planSlug: cachedData.planSlug,
            productId: cachedData.productId,
            subscriptionStart: cachedData.subscriptionStart,
            subscriptionEnd: cachedData.subscriptionEnd,
            cancelAtPeriodEnd: cachedData.cancelAtPeriodEnd,
            usage,
            priceCents: cachedData.priceCents,
            billingInterval: cachedData.billingInterval,
            billingIntervalCount: cachedData.billingIntervalCount
          });
          return;
        }
      }

      // Add timeout to prevent hanging indefinitely
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Subscription check timed out')), 15000)
      );

      const fetchPromise = (async () => {
        const res = await fetch('/api/stripe/check-subscription', { method: 'POST' });
        const data = await res.json().catch(() => null);
        if (!res.ok) {
          throw new Error(data?.error || `HTTP ${res.status}`);
        }
        return { data, error: null as null | { message?: string } };
      })();

      const { data, error } = await Promise.race([fetchPromise, timeoutPromise]) as { data: any; error: { message?: string } | null };

      if (error) {
        console.error('Error checking subscription:', error);
        // If auth error, clear the stale cache and state
        if (error.message?.includes('Auth') || error.message?.includes('session') || error.message?.includes('401') || error.message?.includes('403')) {
          clearCachedSubscription();
          setState({
            isLoading: false,
            isSubscribed: false,
            planSlug: null,
            productId: null,
            subscriptionStart: null,
            subscriptionEnd: null,
            cancelAtPeriodEnd: false,
            usage: {},
            priceCents: null,
            billingInterval: null,
            billingIntervalCount: null
          });
        } else {
          setState(prev => ({ ...prev, isLoading: false }));
        }
        return;
      }

      // Fetch current usage
      const now = new Date();
      const periodStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
      
      const { data: usageData } = await supabase
        .from('usage_tracking')
        .select('feature_type, count')
        .eq('user_id', session.user.id)
        .gte('period_start', periodStart);

      const usage: Record<string, number> = {};
      if (usageData) {
        (usageData as UsageRecord[]).forEach((record) => {
          usage[record.feature_type] = record.count;
        });
      }

      const subscriptionData = {
        isSubscribed: data?.subscribed ?? false,
        planSlug: data?.plan_slug as PlanSlug | null,
        productId: data?.product_id ?? null,
        subscriptionStart: data?.current_period_start ?? null,
        subscriptionEnd: data?.subscription_end ?? null,
        cancelAtPeriodEnd: data?.cancel_at_period_end ?? false,
        priceCents: data?.price_cents ?? null,
        billingInterval: data?.billing_interval ?? null,
        billingIntervalCount: data?.billing_interval_count ?? null,
      };

      // Cache the subscription data
      setCachedSubscription(subscriptionData);

      setState({
        isLoading: false,
        ...subscriptionData,
        usage
      });
    } catch (error) {
      console.error('Error in checkSubscription:', error);
      setState(prev => ({ ...prev, isLoading: false }));
    }
  }, []);

  const checkUsage = useCallback((featureType: string): { canUse: boolean; remaining: number; limit: number } => {
    if (!state.isSubscribed || !state.planSlug) {
      return { canUse: false, remaining: 0, limit: 0 };
    }

    const plan = SUBSCRIPTION_PLANS[state.planSlug];
    const limit = plan.limits[featureType as keyof typeof plan.limits] || 0;
    const used = state.usage[featureType] || 0;
    const remaining = Math.max(0, limit - used);

    return { canUse: remaining > 0, remaining, limit };
  }, [state.isSubscribed, state.planSlug, state.usage]);

  const incrementUsage = useCallback(async (featureType: string): Promise<boolean> => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return false;

      const now = new Date();
      const periodStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const periodEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);

      const currentUsage = state.usage[featureType] || 0;

      const { error } = await supabase
        .from('usage_tracking')
        .upsert({
          user_id: session.user.id,
          feature_type: featureType,
          count: currentUsage + 1,
          period_start: periodStart.toISOString(),
          period_end: periodEnd.toISOString()
        }, { 
          onConflict: 'user_id,feature_type,period_start'
        });

      if (error) {
        console.error('Error incrementing usage:', error);
        return false;
      }

      setState(prev => ({
        ...prev,
        usage: {
          ...prev.usage,
          [featureType]: currentUsage + 1
        }
      }));

      return true;
    } catch (error) {
      console.error('Error in incrementUsage:', error);
      return false;
    }
  }, [state.usage]);

  /**
   * Starts Stripe Checkout. Resolves `true` once the tab has been handed over
   * to Stripe — callers should leave their loading state up in that case,
   * because the browser is still fetching the Stripe page and clearing the
   * spinner would read as "nothing happened" a second time.
   */
  const createCheckout = useCallback(async (priceId: string): Promise<boolean> => {
    try {
      const res = await fetch('/api/stripe/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ priceId }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        toast({
          title: "Error",
          description: data?.error || "Failed to create checkout session",
          variant: "destructive"
        });
        return false;
      }

      // Same-tab navigation — a popup here is blocked on iOS. See
      // redirectToExternalUrl for the full explanation.
      if (!redirectToExternalUrl(data?.url)) {
        toast({
          title: "Error",
          description: "Could not open Stripe Checkout. Please try again.",
          variant: "destructive"
        });
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast({
        title: "Error",
        description: "Failed to create checkout session",
        variant: "destructive"
      });
      return false;
    }
  }, [toast]);

  /** Same contract as createCheckout — see its note on the return value. */
  const openCustomerPortal = useCallback(async (): Promise<boolean> => {
    try {
      const res = await fetch('/api/stripe/portal', { method: 'POST' });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        toast({
          title: "Error",
          description: data?.error || "Failed to open customer portal",
          variant: "destructive"
        });
        return false;
      }

      // Same-tab navigation — a popup here is blocked on iOS. See
      // redirectToExternalUrl for the full explanation.
      if (!redirectToExternalUrl(data?.url)) {
        toast({
          title: "Error",
          description: "Could not open the billing portal. Please try again.",
          variant: "destructive"
        });
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error opening customer portal:', error);
      toast({
        title: "Error",
        description: "Failed to open customer portal",
        variant: "destructive"
      });
      return false;
    }
  }, [toast]);

  useEffect(() => {
    checkSubscription();

    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_IN') {
        // Force refresh on sign in to get fresh data
        checkSubscription(true);
      } else if (event === 'TOKEN_REFRESHED') {
        checkSubscription();
      } else if (event === 'SIGNED_OUT') {
        clearCachedSubscription();
        setState({
          isLoading: false,
          isSubscribed: false,
          planSlug: null,
          productId: null,
          subscriptionStart: null,
          subscriptionEnd: null,
          cancelAtPeriodEnd: false,
          usage: {},
          priceCents: null,
          billingInterval: null,
          billingIntervalCount: null
        });
      }
    });

    // Auto-refresh every hour (cache handles most cases)
    const interval = setInterval(() => checkSubscription(), 60 * 60 * 1000);

    return () => {
      subscription.unsubscribe();
      clearInterval(interval);
    };
  }, [checkSubscription]);

  return {
    ...state,
    checkSubscription,
    checkUsage,
    incrementUsage,
    createCheckout,
    openCustomerPortal,
    currentPlan: state.planSlug ? SUBSCRIPTION_PLANS[state.planSlug] : null
  };
}
